// src/pages/calidad/AuditoriaCaptura.jsx
import { useState } from 'react';
import api from '../../services/api';
import EditorTexto from '../../components/EditorTexto';

function vacio() { return '<p></p>'; }
function filaVaciaNC() { return { requisito: '', situacion_deseada: vacio(), situacion_actual: vacio() }; }
function filaVaciaTexto() { return { descripcion: vacio() }; }

export default function AuditoriaCaptura() {
  const [form, setForm] = useState({
    fecha_inicio: '', fecha_fin: '', area_proceso: vacio(), alcance: vacio(), objetivo: vacio(),
    auditor_lider: '', auditores: '', observadores: '', auditados: '', resumen: vacio(),
  });
  const [noConformidades, setNoConformidades] = useState([filaVaciaNC()]);
  const [observaciones, setObservaciones] = useState([filaVaciaTexto()]);
  const [oportunidades, setOportunidades] = useState([filaVaciaTexto()]);
  const [fortalezas, setFortalezas] = useState([filaVaciaTexto()]);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [pdfNombre, setPdfNombre] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  function actualizarCampo(campo, valor) {
    setForm((prev) => ({ ...prev, [campo]: valor }));
  }

  function actualizarNC(i, campo, valor) {
    setNoConformidades((prev) => prev.map((f, idx) => (idx === i ? { ...f, [campo]: valor } : f)));
  }
  function agregarNC() { setNoConformidades((prev) => [...prev, filaVaciaNC()]); }
  function quitarNC(i) { setNoConformidades((prev) => prev.filter((_, idx) => idx !== i)); }

  function crearListaHandlers(lista, setLista) {
    return {
      actualizar: (i, valor) => setLista((prev) => prev.map((f, idx) => (idx === i ? { descripcion: valor } : f))),
      agregar: () => setLista((prev) => [...prev, filaVaciaTexto()]),
      quitar: (i) => setLista((prev) => prev.filter((_, idx) => idx !== i)),
    };
  }
  const obsHandlers = crearListaHandlers(observaciones, setObservaciones);
  const opHandlers = crearListaHandlers(oportunidades, setOportunidades);
  const fortHandlers = crearListaHandlers(fortalezas, setFortalezas);

  function handleArchivo(e) {
    const archivo = e.target.files?.[0];
    if (!archivo) return;
    if (archivo.type !== 'application/pdf') {
      setMensaje({ tipo: 'error', texto: 'Solo se aceptan archivos PDF.' });
      return;
    }
    const lector = new FileReader();
    lector.onload = () => {
      setPdfBase64(lector.result.split(',')[1]);
      setPdfNombre(archivo.name);
      setMensaje(null);
    };
    lector.readAsDataURL(archivo);
  }

  function esVacio(html) {
    if (!html) return true;
    const texto = html.replace(/<(.|\n)*?>/g, '').trim();
    return texto.length === 0;
  }

  async function handleGuardar() {
    if (!form.fecha_inicio) {
      setMensaje({ tipo: 'error', texto: 'La fecha de inicio de la auditoría es obligatoria.' });
      return;
    }
    try {
      setGuardando(true);
      setMensaje(null);
      const payload = {
        ...form,
        no_conformidades: noConformidades.filter((f) => !esVacio(f.situacion_actual) || f.requisito.trim()),
        observaciones: observaciones.filter((f) => !esVacio(f.descripcion)),
        oportunidades: oportunidades.filter((f) => !esVacio(f.descripcion)),
        fortalezas: fortalezas.filter((f) => !esVacio(f.descripcion)),
        pdf_base64: pdfBase64,
        pdf_nombre_archivo: pdfNombre,
      };
      await api.post('/api/calidad/auditorias', payload);
      setMensaje({ tipo: 'ok', texto: '✓ Auditoría capturada correctamente.' });
      setForm({
        fecha_inicio: '', fecha_fin: '', area_proceso: vacio(), alcance: vacio(), objetivo: vacio(),
        auditor_lider: '', auditores: '', observadores: '', auditados: '', resumen: vacio(),
      });
      setNoConformidades([filaVaciaNC()]);
      setObservaciones([filaVaciaTexto()]);
      setOportunidades([filaVaciaTexto()]);
      setFortalezas([filaVaciaTexto()]);
      setPdfBase64(null);
      setPdfNombre(null);
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo guardar la auditoría.' });
    } finally {
      setGuardando(false);
    }
  }

  function SeccionLista({ titulo, items, handlers, nota, placeholder }) {
    const cuenta = items.filter((f) => !esVacio(f.descripcion)).length;
    return (
      <div style={styles.tarjeta}>
        <div style={styles.seccionTitulo}>{titulo} <span style={styles.badgeCuenta}>{cuenta}</span></div>
        {nota && <p style={styles.notaObs}>{nota}</p>}
        {items.map((fila, i) => (
          <div key={i} style={styles.filaObs}>
            <div style={styles.numeroFila}>{i + 1}</div>
            <div style={{ flex: 1 }}>
              <EditorTexto value={fila.descripcion} onChange={(v) => handlers.actualizar(i, v)} placeholder={placeholder} />
            </div>
            {items.length > 1 && (
              <button onClick={() => handlers.quitar(i)} style={styles.botonQuitar} title="Quitar fila">✕</button>
            )}
          </div>
        ))}
        <button onClick={handlers.agregar} style={styles.botonAgregar}>+ Agregar</button>
      </div>
    );
  }

  return (
    <div style={styles.contenedor} className="contenedor-captura">
      <h1 style={styles.titulo}>Captura de Auditoría (F-SGI-12)</h1>
      <p style={styles.subtitulo}>Registra el informe de auditoría interna — encabezado, No Conformidades, Observaciones, Oportunidades y Fortalezas.</p>

      {mensaje && <div style={{ ...styles.mensaje, color: mensaje.tipo === 'ok' ? '#3E7C5A' : '#B23A48' }}>{mensaje.texto}</div>}

      <div style={styles.tarjeta}>
        <div style={styles.seccionTitulo}>Datos generales</div>
        <div className="fila2">
          <div style={styles.grupo}>
            <label style={styles.label}>Fecha de inicio *</label>
            <input type="date" value={form.fecha_inicio} onChange={(e) => actualizarCampo('fecha_inicio', e.target.value)} style={styles.input} />
          </div>
          <div style={styles.grupo}>
            <label style={styles.label}>Fecha de fin (opcional)</label>
            <input type="date" value={form.fecha_fin} onChange={(e) => actualizarCampo('fecha_fin', e.target.value)} style={styles.input} />
          </div>
        </div>
        <div style={styles.grupo}>
          <label style={styles.label}>Área o proceso</label>
          <EditorTexto value={form.area_proceso} onChange={(v) => actualizarCampo('area_proceso', v)} placeholder="Ej. Calidad y Calidad de materiales" />
        </div>
        <div style={styles.grupo}>
          <label style={styles.label}>Alcance / criterios de auditoría</label>
          <EditorTexto value={form.alcance} onChange={(v) => actualizarCampo('alcance', v)} placeholder="Criterios de auditoría..." />
        </div>
        <div style={styles.grupo}>
          <label style={styles.label}>Objetivo de la auditoría</label>
          <EditorTexto value={form.objetivo} onChange={(v) => actualizarCampo('objetivo', v)} placeholder="Objetivo de la auditoría..." />
        </div>
        <div className="fila2">
          <div style={styles.grupo}>
            <label style={styles.label}>Auditor líder</label>
            <input type="text" value={form.auditor_lider} onChange={(e) => actualizarCampo('auditor_lider', e.target.value)} style={styles.input} />
          </div>
          <div style={styles.grupo}>
            <label style={styles.label}>Auditores</label>
            <input type="text" value={form.auditores} onChange={(e) => actualizarCampo('auditores', e.target.value)} style={styles.input} />
          </div>
        </div>
        <div className="fila2">
          <div style={styles.grupo}>
            <label style={styles.label}>Observadores</label>
            <input type="text" value={form.observadores} onChange={(e) => actualizarCampo('observadores', e.target.value)} style={styles.input} placeholder="N/A si no aplica" />
          </div>
          <div style={styles.grupo}>
            <label style={styles.label}>Auditados</label>
            <input type="text" value={form.auditados} onChange={(e) => actualizarCampo('auditados', e.target.value)} style={styles.input} />
          </div>
        </div>
        <div style={styles.grupo}>
          <label style={styles.label}>Resumen de la auditoría</label>
          <EditorTexto value={form.resumen} onChange={(v) => actualizarCampo('resumen', v)} placeholder="Resumen de la auditoría..." />
        </div>
      </div>

      <div style={styles.tarjeta}>
        <div style={styles.seccionTitulo}>No Conformidades <span style={styles.badgeCuenta}>{noConformidades.filter((f) => !esVacio(f.situacion_actual) || f.requisito.trim()).length}</span></div>
        {noConformidades.map((fila, i) => (
          <div key={i} style={styles.filaNC}>
            <div style={styles.numeroFila}>{i + 1}</div>
            <div style={styles.gridNC}>
              <input type="text" placeholder="Requisito (ej. ISO 22000 8.2.1)" value={fila.requisito} onChange={(e) => actualizarNC(i, 'requisito', e.target.value)} style={styles.input} />
              <div>
                <label style={styles.labelChico}>Situación deseada</label>
                <EditorTexto value={fila.situacion_deseada} onChange={(v) => actualizarNC(i, 'situacion_deseada', v)} />
              </div>
              <div>
                <label style={styles.labelChico}>Situación actual</label>
                <EditorTexto value={fila.situacion_actual} onChange={(v) => actualizarNC(i, 'situacion_actual', v)} />
              </div>
            </div>
            {noConformidades.length > 1 && (
              <button onClick={() => quitarNC(i)} style={styles.botonQuitar} title="Quitar fila">✕</button>
            )}
          </div>
        ))}
        <button onClick={agregarNC} style={styles.botonAgregar}>+ Agregar No Conformidad</button>
      </div>

      <SeccionLista
        titulo="Observaciones"
        items={observaciones}
        handlers={obsHandlers}
        nota="Las Observaciones no entran al conteo del KPI — solo son referencia del informe."
        placeholder="Descripción de la observación..."
      />
      <SeccionLista titulo="Oportunidades de Mejora" items={oportunidades} handlers={opHandlers} placeholder="Oportunidad de mejora..." />
      <SeccionLista titulo="Fortalezas" items={fortalezas} handlers={fortHandlers} placeholder="Fortaleza identificada..." />

      <div style={styles.tarjeta}>
        <div style={styles.grupo}>
          <label style={styles.label}>PDF del informe original (opcional)</label>
          <input type="file" accept="application/pdf" onChange={handleArchivo} style={styles.input} />
          {pdfNombre && <span style={styles.pdfCargado}>✓ {pdfNombre}</span>}
        </div>
      </div>

      <button onClick={handleGuardar} disabled={guardando || !form.fecha_inicio} style={styles.botonGuardar}>
        {guardando ? 'Guardando...' : 'Guardar auditoría'}
      </button>
    </div>
  );
}

const styles = {
  contenedor: { fontFamily: 'Inter, sans-serif', padding: '24px', color: '#3A2E42', maxWidth: '760px' },
  titulo: { fontFamily: 'Playfair Display, serif', color: '#2A1830', marginBottom: '4px' },
  subtitulo: { fontSize: '12px', color: '#8A7690', marginBottom: '18px' },
  mensaje: { fontSize: '13px', marginBottom: '14px', fontWeight: 600 },
  tarjeta: { background: '#fff', border: '1px solid #E6DDD2', borderRadius: '12px', padding: '18px', boxShadow: '0 2px 14px rgba(74,30,92,0.07)', display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '16px' },
  seccionTitulo: { fontFamily: 'Playfair Display, serif', fontSize: '15px', color: '#2A1830', display: 'flex', alignItems: 'center', gap: '8px' },
  badgeCuenta: { fontSize: '11px', fontWeight: 700, background: '#EFE9F3', color: '#6A2C75', borderRadius: '10px', padding: '2px 8px' },
  grupo: { display: 'flex', flexDirection: 'column', gap: '5px' },
  label: { fontSize: '12px', fontWeight: 600, color: '#6E4F80' },
  labelChico: { fontSize: '11px', fontWeight: 600, color: '#8A7690', display: 'block', marginBottom: '4px' },
  input: { padding: '8px 10px', fontSize: '13px', border: '1px solid #D6A644', borderRadius: '6px', fontFamily: 'Inter, sans-serif', background: '#FFFDF7' },
  filaNC: { display: 'flex', gap: '10px', alignItems: 'flex-start', paddingBottom: '14px', borderBottom: '1px solid #F0E9E0' },
  gridNC: { display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 },
  filaObs: { display: 'flex', gap: '10px', alignItems: 'flex-start' },
  numeroFila: { width: '22px', height: '22px', borderRadius: '50%', background: '#EFE9F3', color: '#6A2C75', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '4px' },
  botonQuitar: { background: 'transparent', border: 'none', color: '#B23A48', cursor: 'pointer', fontSize: '14px', flexShrink: 0, marginTop: '4px' },
  botonAgregar: { alignSelf: 'flex-start', padding: '6px 14px', fontSize: '12px', fontWeight: 600, color: '#6E4F80', background: '#EFE9F3', border: '1px solid #A98BB8', borderRadius: '6px', cursor: 'pointer' },
  notaObs: { fontSize: '11px', color: '#8A7690', margin: 0 },
  pdfCargado: { fontSize: '12px', color: '#3E7C5A', fontWeight: 600 },
  botonGuardar: { width: '100%', padding: '12px', fontSize: '14px', fontWeight: 700, color: '#fff', background: '#6A2C75', border: 'none', borderRadius: '8px', cursor: 'pointer' },
};